import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { queryKeys } from "@/lib/query-keys";

export interface UsageLimits {
  usage: {
    tripsCreated: number;
    packingGenerated: number;
    tripsPublished: number;
  };
  limits: {
    tripsCreated: number;
    packingGenerated: number;
    tripsPublished: number;
  } | null;
  isPremium: boolean;
  premiumTier: string;
  creditBalance: number;
  creditCosts: {
    tripCreate: number;
    tripPublish: number;
    packingList: number;
    favoriteTraveler: number;
    photoUpload: number;
    videoUpload: number;
    aiChat: number;
    aiSearch: number;
    aiPriceVerification: number;
    aiItineraryRating: number;
  };
  tierBudgets?: {
    monthlyCredits: number;
    groundingDays: number;
    freePhotos: number;
    freeVideos: number;
  };
}

export function useUsageLimits() {
  const { user } = useAuth();

  const query = useQuery<UsageLimits>({
    queryKey: queryKeys.usage.all,
    enabled: !!user,
  });

  const creditBalance = query.data?.creditBalance ?? 0;
  const creditCosts = query.data?.creditCosts ?? { tripCreate: 1, tripPublish: 1, packingList: 1, favoriteTraveler: 1, photoUpload: 1, videoUpload: 2, aiChat: 1, aiSearch: 1, aiPriceVerification: 3, aiItineraryRating: 1 };

  const canCreateTrip = query.data?.isPremium || 
    (query.data?.usage?.tripsCreated ?? 0) < (query.data?.limits?.tripsCreated ?? 10);
  
  const canCreateTripWithCredits = !canCreateTrip && creditBalance >= creditCosts.tripCreate;

  const canGeneratePacking = query.data?.isPremium || 
    (query.data?.usage?.packingGenerated ?? 0) < (query.data?.limits?.packingGenerated ?? 10);

  const canGeneratePackingWithCredits = !canGeneratePacking && creditBalance >= creditCosts.packingList;

  const canPublishTrip = query.data?.isPremium || 
    (query.data?.usage?.tripsPublished ?? 0) < (query.data?.limits?.tripsPublished ?? 3);

  const canPublishTripWithCredits = !canPublishTrip && creditBalance >= creditCosts.tripPublish;

  const tripsCreatedRemaining = query.data?.isPremium ? Infinity :
    Math.max(0, (query.data?.limits?.tripsCreated ?? 10) - (query.data?.usage?.tripsCreated ?? 0));

  const packingRemaining = query.data?.isPremium ? Infinity :
    Math.max(0, (query.data?.limits?.packingGenerated ?? 10) - (query.data?.usage?.packingGenerated ?? 0));

  const publishRemaining = query.data?.isPremium ? Infinity :
    Math.max(0, (query.data?.limits?.tripsPublished ?? 3) - (query.data?.usage?.tripsPublished ?? 0));

  return {
    ...query,
    canCreateTrip,
    canCreateTripWithCredits,
    canGeneratePacking,
    canGeneratePackingWithCredits,
    canPublishTrip,
    canPublishTripWithCredits,
    tripsCreatedRemaining,
    packingRemaining,
    publishRemaining,
    isPremium: query.data?.isPremium ?? false,
    creditBalance,
    creditCosts,
  };
}
